import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

export default function ResetPasswordScreen({ onComplete }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    // Supabase auto-handles the token from URL hash fragments
    // We just need to wait for the PASSWORD_RECOVERY event or check session
    const checkSession = async () => {
      // Give Supabase a moment to process the URL tokens
      await new Promise(r => setTimeout(r, 1500));
      
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSessionReady(true);
        console.log('[ResetPassword] Session ready for user:', session.user.id);
      } else {
        setExpired(true);
        console.log('[ResetPassword] No session — token may be expired');
      }
    };

    // Listen for PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
        setExpired(false);
        console.log('[ResetPassword] PASSWORD_RECOVERY event received');
      }
    });

    checkSession();

    return () => subscription.unsubscribe();
  }, []);

  const handleReset = async () => {
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setBusy(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({
        password: password
      });

      if (updateErr) {
        setError(updateErr.message || "Failed to update password");
        setBusy(false);
        return;
      }

      setSuccess(true);
      // Redirect to login after 2 seconds
      setTimeout(() => {
        onComplete();
      }, 2500);
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setBusy(false);
  };

  const EyeIcon = ({ open }) =>
    open ? (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );

  // Expired / invalid link
  if (expired) {
    return (
      <div className="auth-wrapper fade-in" data-testid="reset-password-expired">
        <div className="auth-card" style={{ background: '#FFFFFF' }}>
          <div className="auth-logo" style={{ background: 'transparent' }}>
            <img src="/logo_horizontal.jpg" alt="PCOS Insights"
              style={{ maxWidth: 260, width: '85%', height: 'auto', objectFit: 'contain' }} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: '#FEE2E2', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 20px'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#2F2F35', marginBottom: 8 }}>
              Link Expired
            </h2>
            <p style={{ fontSize: 14, color: '#5A5A5A', lineHeight: 1.6, marginBottom: 24 }}>
              This password reset link has expired or is invalid. Please request a new one.
            </p>
            <button className="btn-primary" onClick={onComplete}
              data-testid="back-to-login-expired" style={{ width: '100%' }}>
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Waiting for session
  if (!sessionReady) {
    return (
      <div className="auth-wrapper fade-in" data-testid="reset-password-loading">
        <div className="auth-card" style={{ background: '#FFFFFF', textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '3px solid #EEEAF8', borderTopColor: '#6D5EF5',
            animation: 'spin 0.8s linear infinite', margin: '40px auto 16px'
          }} />
          <p style={{ color: '#8A8A9A', fontSize: 14 }}>Verifying reset link...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Success
  if (success) {
    return (
      <div className="auth-wrapper fade-in" data-testid="reset-password-success">
        <div className="auth-card" style={{ background: '#FFFFFF' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: '#D1FAE5', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 20px'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#2F2F35', marginBottom: 8 }}>
              Password Updated
            </h2>
            <p style={{ fontSize: 14, color: '#5A5A5A', marginBottom: 16 }}>
              Your password has been updated successfully.
            </p>
            <p style={{ fontSize: 13, color: '#8A8A9A' }}>
              Redirecting to sign in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="auth-wrapper fade-in" data-testid="reset-password-screen">
      <div className="auth-card" style={{ background: '#FFFFFF' }}>
        <div className="auth-logo" style={{ background: 'transparent' }}>
          <img src="/logo_horizontal.jpg" alt="PCOS Insights"
            style={{ maxWidth: 260, width: '85%', height: 'auto', objectFit: 'contain' }} />
        </div>

        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#2F2F35', textAlign: 'center', marginBottom: 4 }}>
          Create New Password
        </h2>
        <p style={{ fontSize: 14, color: '#8A8A9A', textAlign: 'center', marginBottom: 24 }}>
          Enter your new password below
        </p>

        <div className="auth-form">
          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              data-testid="reset-password-input"
            />
            <button type="button" className="eye-toggle"
              onClick={() => setShowPassword(!showPassword)}>
              <EyeIcon open={showPassword} />
            </button>
          </div>
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input-field"
            data-testid="reset-confirm-input"
            onKeyDown={(e) => e.key === 'Enter' && handleReset()}
          />

          {password && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              {[1,2,3,4].map(i => (
                <div key={i} style={{
                  flex: 1, height: 3, borderRadius: 2,
                  background: password.length >= i * 3 ? 
                    (password.length >= 10 ? '#059669' : password.length >= 8 ? '#F59E0B' : '#EF4444') 
                    : '#E5E7EB'
                }} />
              ))}
            </div>
          )}

          {error && <p className="auth-error" data-testid="reset-error">{error}</p>}

          <button
            className="btn-primary"
            onClick={handleReset}
            disabled={busy}
            data-testid="update-password-btn"
            style={{ width: '100%' }}
          >
            {busy ? "Updating..." : "Update Password"}
          </button>
        </div>
      </div>
    </div>
  );
}
